import React, { useRef } from 'react';
import styled from 'styled-components';
import { IconButton, Popover } from '@mui/material';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import Button from '../../../../../src/components/library/Button';
import Tooltip from '../../../../../src/components/library/Tooltip';
import { BrokerJob } from '../../core';
import { brokerJobsStoreSelector, isJobCancellable, useBrokerJobsStore } from '../../domain/broker-jobs.store';

interface ActionsCellProps {
	job: BrokerJob;
	/** The object whose Logs tab shows the job; omit outside an object context (e.g. the Service Log page). */
	nxObjectUuid?: string;
	/** Called after a cancellation was confirmed and sent, so a non-object list can refresh itself. */
	onCancelled?: (jobId: string) => void;
}

const Wrapper = styled('div')({
	alignItems: 'center',
	display: 'flex',
	height: '100%',
	justifyContent: 'center',
	width: '100%',
});

const CancelConfirmCard = styled('div')(({ theme }) => ({
	padding: theme.spacing(1.75),
	width: 230,
}));

const CancelConfirmTitle = styled('div')(({ theme }) => ({
	color: theme.palette.text.primary,
	fontSize: theme.typography.bodyMediumBold.fontSize,
	marginBottom: theme.spacing(0.5),
}));

const CancelConfirmDescription = styled('div')(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontSize: theme.typography.bodySmallRegular.fontSize,
	lineHeight: 1.4,
	marginBottom: theme.spacing(1.5),
}));

const CancelConfirmActions = styled('div')(({ theme }) => ({
	display: 'flex',
	gap: theme.spacing(1),
	justifyContent: 'flex-end',
}));

export function ActionsCell({ job, nxObjectUuid, onCancelled }: ActionsCellProps) {
	const anchorRef = useRef<HTMLButtonElement>(null);
	const cancelConfirmJobId = useBrokerJobsStore(brokerJobsStoreSelector.cancelConfirmJobId);
	const { askCancelJob, confirmCancelJob } = useBrokerJobsStore(brokerJobsStoreSelector.actions);

	if (!isJobCancellable(job)) return null;

	const isConfirmingCancel = cancelConfirmJobId === job.jobId;

	return (
		<Wrapper onClick={(event) => event.stopPropagation()}>
			<Tooltip title="Cancel job">
				<IconButton
					ref={anchorRef}
					size="small"
					aria-label="Cancel-Button"
					onClick={() => askCancelJob(isConfirmingCancel ? null : job.jobId)}
				>
					<StopCircleIcon color="error" fontSize="small" />
				</IconButton>
			</Tooltip>
			<Popover
				open={isConfirmingCancel}
				anchorEl={anchorRef.current}
				onClose={() => askCancelJob(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<CancelConfirmCard>
					<CancelConfirmTitle>Cancel this job?</CancelConfirmTitle>
					<CancelConfirmDescription>
						{job.serviceName} will be removed from the queue and marked Cancelled.
					</CancelConfirmDescription>
					<CancelConfirmActions>
						<Button nxstyle="secondary-black" size="small" onClick={() => askCancelJob(null)}>
							Keep
						</Button>
						<Button
							nxstyle="primary-red"
							size="small"
							onClick={async () => {
								await confirmCancelJob(nxObjectUuid ?? null, job.jobId);
								onCancelled?.(job.jobId);
							}}
						>
							Cancel Job
						</Button>
					</CancelConfirmActions>
				</CancelConfirmCard>
			</Popover>
		</Wrapper>
	);
}
